import { FC, useMemo } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { formatDate, prettifyAmount } from '@utils/index';
import Status from '@components/common/status';
import { TGetOrderById, TOrder } from '@xeppt/xeppt-sdk/types';
import type { TOrderWithReferenceCode } from 'src/types/reference_code';
import { modalIds } from '@const/modals';
import { currencies } from '@const/currency';
import useModalContext from '@hooks/context/useModalContext';
import { apiOrderService } from '@api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApiMutation } from '@hooks/api/useApiMutation';

// Backend returns id + customer_display_name / customer_id / currency that the SDK
// type omits. reference_code is centralized in src/types/reference_code.ts (see that
// file's header for the gated cleanup once @xeppt/xeppt-sdk republishes it) — the
// rest of these gap fields stay local since they're only needed in this component.
type TOrderWithId = TOrderWithReferenceCode & {
    id: string;
    currency?: string;
    customer_display_name?: string;
    customer_id?: string;
    // Denormalized refund totals (decimal strings, "0.00" when never refunded)
    // the list response now returns — used to derive the Refunded / Partially
    // refunded badge, since order.status has no refunded state.
    refunded_amount?: string;
    remaining_amount?: string;
};

function nameInitials(displayName: string): string {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
}

function handleInitials(handle: string): string {
    const clean = handle.replace(/^@/, '');
    if (!clean) return '??';
    const parts = clean.split(/[._-]/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase();
}

function orderStatusLabel(status: string): string {
    switch (status) {
        case 'succeeded': return 'Paid';
        case 'pending': return 'Pending';
        case 'cancelled': return 'Canceled';
        case 'refunded': return 'Refunded';
        case 'partially_refunded': return 'Partially refunded';
        case 'expired': return 'Expired';
        default: return status;
    }
}

function orderStatusVariant(
    status: string
): 'success' | 'warning' | 'error' | 'gray' {
    switch (status) {
        case 'succeeded': return 'success';
        case 'pending': return 'warning';
        case 'cancelled':
        case 'expired': return 'error';
        default: return 'gray';
    }
}

interface IProps {
    order: TOrder;
    refetchOrders?: () => void;
}

const OrderItem: FC<IProps> = ({ order, refetchOrders }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { changeModalData, onOpen } = useModalContext();
    const o = order as TOrderWithId;
    // Empty string counts as "no alias" here too — always fall back to the raw id.
    const orderIdDisplay = o.reference_code || `#${order.order_id}`;
    const currencySymbol = currencies[o.currency as 'CAD']?.symbol ?? '$';

    const initials = useMemo(() => {
        if (o.customer_display_name) return nameInitials(o.customer_display_name);
        return handleInitials(o.customer_id ?? '');
    }, [o.customer_display_name, o.customer_id]);

    const subline = useMemo(() => {
        const namePart = o.customer_display_name
            ? `${o.customer_display_name}${o.customer_id ? ` (@${o.customer_id})` : ''}`
            : o.customer_id
            ? `@${o.customer_id}`
            : '';
        return namePart;
    }, [o.customer_display_name, o.customer_id]);

    // A fully/partially refunded order still reports order.status "succeeded",
    // so derive the refund state from the refunded/remaining amounts and reuse
    // the existing label/variant switches (which already cover both cases).
    const effectiveStatus = useMemo(() => {
        const refunded = parseFloat(o.refunded_amount ?? '');
        const remaining = parseFloat(o.remaining_amount ?? '');
        if (!isNaN(refunded) && refunded > 0) {
            return !isNaN(remaining) && remaining <= 0 ? 'refunded' : 'partially_refunded';
        }
        return order.status;
    }, [o.refunded_amount, o.remaining_amount, order.status]);

    const { handleRequest: fetchOrder } = useApiMutation({
        method: (id?: string) => apiOrderService.getOrderById(id || ''),
        onSuccess: (data) => {
            handleViewOrder(data);
        }
    });

    const handleViewOrder = (fetchedOrder: TGetOrderById) => {
        changeModalData({ order: fetchedOrder, refetchOrders });
        onOpen({
            modalId: modalIds.VIEW_ORDER,
            onClose: () => navigate(location.pathname)
        });
    };

    return (
        <button
            className={styles.table_item}
            onClick={() => {
                fetchOrder(o.id);
            }}>
            <div className={styles.left}>
                <div className={styles.text_wrapper}>
                    <Typography className={styles.title} variant="body1" weight="semibold">
                        {orderIdDisplay}
                    </Typography>
                    <Typography className={styles.description} variant="body3">
                        {subline && <span>{subline}</span>}
                        {subline && <span className={styles.dot}>●</span>}
                        <i>{formatDate(order.created_at, 'DD MMM YYYY')}</i>
                    </Typography>
                </div>
            </div>
            <div className={styles.right}>
                <Status variant={orderStatusVariant(effectiveStatus)} className={styles.status}>
                    {orderStatusLabel(effectiveStatus)}
                </Status>
                <Typography variant="body1" weight="bold" className={styles.amount}>
                    {currencySymbol}
                    {prettifyAmount(order.amount)}
                </Typography>
            </div>
        </button>
    );
};

export default OrderItem;
